import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { StyleTemplate } from "@/features/studio-ai/types/studio-ai.types"
import type { AxiosError } from "axios"

import {
  createStyleTemplate,
  deleteStyleTemplate,
  listStyleTemplates,
  updateStyleTemplate,
} from "@/features/studio-ai/api/style-templates-api"
import { queryKeys } from "@/lib/query-keys"

type ApiErrorPayload = {
  error?: string
}

export function getStyleTemplateErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<ApiErrorPayload>
  return axiosError.response?.data.error || axiosError.message || "An error occurred"
}

export function useStyleTemplates(params?: { visibility?: string; media_type?: string }) {
  return useQuery({
    queryKey: queryKeys.sora2.styleTemplates(params as Record<string, string> | undefined),
    queryFn: () => listStyleTemplates(params),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  })
}

export function useCreateStyleTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })

  return useMutation({
    mutationFn: createStyleTemplate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sora2.styleTemplates() })
      toast.success(t("created"))
    },
    onError: (error: Error) => {
      toast.error(t("createFailed"), { description: getStyleTemplateErrorMessage(error) })
    },
  })
}

export function useUpdateStyleTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StyleTemplate> }) =>
      updateStyleTemplate(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sora2.styleTemplates() })
      toast.success(t("updated"))
    },
    onError: (error: Error) => {
      toast.error(t("updateFailed"), { description: getStyleTemplateErrorMessage(error) })
    },
  })
}

export function useDeleteStyleTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })

  return useMutation({
    mutationFn: deleteStyleTemplate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sora2.styleTemplates() })
      toast.success(t("deleted"))
    },
    onError: (error: Error) => {
      toast.error(t("deleteFailed"), { description: getStyleTemplateErrorMessage(error) })
    },
  })
}
